'use client';

import { c as _c } from "react/compiler-runtime";
import type { EditorView } from '@codemirror/view';
import { ClientThemeProvider } from '@wrksz/themes/client';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ClientUploadedFileData } from 'uploadthing/types';
import { useMediaQuery } from 'usehooks-ts';
import { MarkdownEditorRestoreContext } from '../../_context/MarkdownEditorRestoreContext';
import { type DbAttachment, mapAttachmentsToFiles } from '../Attachments';
import { EditorLoader, SingleViewEditor, SplitViewEditor } from './_components';
import { useEditorLocalStorage } from './_hooks/useEditorLocalStorage';
import { useEditorRestoreState } from './_hooks/useEditorRestoreState';
import { ClientPreview } from './ClientPreview';
import { Editor } from './Editor';
import { useContentFormatter } from './Editor/_hooks/useContentFormatter';
import { SCROLL_AREA, type TreeData, useSyncScroll } from './Editor/_hooks/useSyncScroll';
export type MarkdownContent = {
  markdown: string;
  attachments?: DbAttachment[];
};
export type MarkdownEditorProps = {
  value?: MarkdownContent;
  onChange?: (value: {
    markdown: string;
    attachments: DbAttachment[];
  }) => void;
  singleView?: boolean;
  height?: number;
  disabled?: boolean;
  markdownOnly?: boolean;
  small?: boolean;
  localStorageEnabled?: boolean;
  unsavedChangesDialog?: React.ReactNode;
};
export const MarkdownEditor: React.FC<MarkdownEditorProps> = t0 => {
  const $ = _c(91);
  const {
    value,
    onChange,
    singleView,
    height,
    disabled: t1,
    markdownOnly: t2,
    small: t3,
    localStorageEnabled: t4,
    unsavedChangesDialog
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const markdownOnly = t2 === undefined ? false : t2;
  const small = t3 === undefined ? false : t3;
  const localStorageEnabled = t4 === undefined ? false : t4;
  const restoreEnabled = localStorageEnabled && unsavedChangesDialog != null;
  const restoreState = useEditorRestoreState(value, onChange, restoreEnabled);
  const [initialValue] = useEditorLocalStorage(value, localStorageEnabled);
  const {
    format,
    isFormatting
  } = useContentFormatter();
  const content = value?.markdown ?? initialValue.markdown;
  value?.attachments;
  const t5 = value?.attachments ?? initialValue.attachments;
  let t6;
  if ($[0] !== t5) {
    t6 = mapAttachmentsToFiles(t5);
    $[0] = t5;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const files = t6;
  const [originalContent, setOriginalContent] = useState(content);
  const [showDiff, setShowDiff] = useState(false);
  const [currentScrollArea, setCurrentScrollArea] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const previewRef = useRef(null);
  const editorViewRef = useRef(null);
  const treeDataRef = useRef(null);
  const editorContainerRef = useRef(null);
  const diffContentRef = useRef(null);
  let t7;
  if ($[2] !== currentScrollArea) {
    t7 = {
      previewRef,
      editorViewRef,
      treeDataRef,
      currentScrollArea,
      editorContainerRef
    };
    $[2] = currentScrollArea;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const {
    handleEditorScroll,
    handlePreviewScroll,
    computePosition,
    getCurrentEditorScrollPosition,
    getCurrentPreviewScrollPosition,
    syncToPreview,
    syncToEditor
  } = useSyncScroll(t7);
  let t8;
  if ($[4] !== files || $[5] !== isFormatting || $[6] !== onChange || $[7] !== showDiff) {
    t8 = value_0 => {
      if (showDiff) {
        diffContentRef.current = value_0;
        return;
      }
      if (!isFormatting) {
        onChange?.({
          markdown: value_0,
          attachments: files.filter(_temp).map(_temp2)
        });
      }
    };
    $[4] = files;
    $[5] = isFormatting;
    $[6] = onChange;
    $[7] = showDiff;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleContentChange = t8;
  let t9;
  if ($[9] !== content || $[10] !== onChange) {
    t9 = newFiles => {
      onChange?.({
        markdown: content,
        attachments: newFiles.map(_temp3)
      });
    };
    $[9] = content;
    $[10] = onChange;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleFilesChange = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = view => {
      editorViewRef.current = view;
      setEditorReady(true);
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const handleCreateEditor = t10;
  let t11;
  if ($[13] !== content || $[14] !== format || $[15] !== onChange || $[16] !== value?.attachments) {
    t11 = async () => {
      const formatted = await format(content);
      if (!formatted) {
        return;
      }
      setOriginalContent(content);
      onChange?.({
        markdown: formatted,
        attachments: value?.attachments ?? []
      });
      setShowDiff(true);
    };
    $[13] = content;
    $[14] = format;
    $[15] = onChange;
    $[16] = value?.attachments;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  value?.attachments;
  const handleFormatContent = t11;
  let t12;
  if ($[18] !== content || $[19] !== onChange || $[20] !== value?.attachments) {
    t12 = () => {
      const finalContent = diffContentRef.current ?? content;
      diffContentRef.current = null;
      setShowDiff(false);
      setOriginalContent(finalContent);
      onChange?.({
        markdown: finalContent,
        attachments: value?.attachments ?? []
      });
    };
    $[18] = content;
    $[19] = onChange;
    $[20] = value?.attachments;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  value?.attachments;
  const handleDiffComplete = t12;
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const isSingleView = singleView ?? isMobile;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setCurrentScrollArea(SCROLL_AREA.EDITOR);
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = !isFormatting && !disabled;
  let t15;
  if ($[23] !== files || $[24] !== handleFilesChange) {
    t15 = attachment => handleFilesChange([...files, attachment]);
    $[23] = files;
    $[24] = handleFilesChange;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== handleFormatContent || $[27] !== singleView) {
    t16 = {
      singleView,
      onFormat: handleFormatContent
    };
    $[26] = handleFormatContent;
    $[27] = singleView;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== content || $[30] !== handleContentChange || $[31] !== handleDiffComplete || $[32] !== handleEditorScroll || $[33] !== originalContent || $[34] !== showDiff || $[35] !== small || $[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <Editor ref={editorContainerRef} content={content} setContent={handleContentChange} originalContent={originalContent} onCreateEditor={handleCreateEditor} onScroll={handleEditorScroll} onMouseEnter={t13} showDiff={showDiff} onDiffComplete={handleDiffComplete} isEditable={t14} small={small} onAddAttachment={t15} passDownProps={t16} />;
    $[29] = content;
    $[30] = handleContentChange;
    $[31] = handleDiffComplete;
    $[32] = handleEditorScroll;
    $[33] = originalContent;
    $[34] = showDiff;
    $[35] = small;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const EditorSection = t17;
  let t18;
  if ($[40] !== content) {
    t18 = {
      markdown: content
    };
    $[40] = content;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setCurrentScrollArea(SCROLL_AREA.PREVIEW);
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setPreviewReady(true);
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== handlePreviewScroll || $[45] !== t18) {
    t21 = <ClientPreview ref={previewRef} content={t18} onScroll={handlePreviewScroll} onMouseEnter={t19} treeDataRef={treeDataRef} onReady={t20} className="px-6" />;
    $[44] = handlePreviewScroll;
    $[45] = t18;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const PreviewSection = t21;
  if (isSingleView) {
    let t22;
    if ($[47] !== editorReady || $[48] !== height) {
      t22 = !editorReady && <EditorLoader height={height} />;
      $[47] = editorReady;
      $[48] = height;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== computePosition || $[51] !== getCurrentEditorScrollPosition || $[52] !== getCurrentPreviewScrollPosition || $[53] !== syncToEditor || $[54] !== syncToPreview) {
      t23 = {
        computePosition,
        getCurrentEditorScrollPosition,
        getCurrentPreviewScrollPosition,
        syncToPreview,
        syncToEditor
      };
      $[50] = computePosition;
      $[51] = getCurrentEditorScrollPosition;
      $[52] = getCurrentPreviewScrollPosition;
      $[53] = syncToEditor;
      $[54] = syncToPreview;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] !== EditorSection || $[57] !== PreviewSection || $[58] !== disabled || $[59] !== editorReady || $[60] !== files || $[61] !== handleFilesChange || $[62] !== height || $[63] !== markdownOnly || $[64] !== small || $[65] !== t23) {
      t24 = <SingleViewEditor editorReady={editorReady} EditorSection={EditorSection} PreviewSection={PreviewSection} height={height} markdownOnly={markdownOnly} files={files} setFiles={handleFilesChange} disabled={disabled} small={small} syncScroll={t23} />;
      $[56] = EditorSection;
      $[57] = PreviewSection;
      $[58] = disabled;
      $[59] = editorReady;
      $[60] = files;
      $[61] = handleFilesChange;
      $[62] = height;
      $[63] = markdownOnly;
      $[64] = small;
      $[65] = t23;
      $[66] = t24;
    } else {
      t24 = $[66];
    }
    let t25;
    if ($[67] !== restoreState || $[68] !== t22 || $[69] !== t24 || $[70] !== unsavedChangesDialog) {
      t25 = <ClientThemeProvider><MarkdownEditorRestoreContext.Provider value={restoreState}>{t22}{t24}{unsavedChangesDialog}</MarkdownEditorRestoreContext.Provider></ClientThemeProvider>;
      $[67] = restoreState;
      $[68] = t22;
      $[69] = t24;
      $[70] = unsavedChangesDialog;
      $[71] = t25;
    } else {
      t25 = $[71];
    }
    return t25;
  }
  let t22;
  if ($[72] !== editorReady || $[73] !== height || $[74] !== previewReady) {
    t22 = !editorReady && !previewReady && <EditorLoader height={height} />;
    $[72] = editorReady;
    $[73] = height;
    $[74] = previewReady;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== EditorSection || $[77] !== PreviewSection || $[78] !== disabled || $[79] !== editorReady || $[80] !== files || $[81] !== handleFilesChange || $[82] !== height || $[83] !== markdownOnly || $[84] !== previewReady) {
    t23 = <SplitViewEditor editorReady={editorReady} previewReady={previewReady} EditorSection={EditorSection} PreviewSection={PreviewSection} height={height} markdownOnly={markdownOnly} files={files} setFiles={handleFilesChange} disabled={disabled} />;
    $[76] = EditorSection;
    $[77] = PreviewSection;
    $[78] = disabled;
    $[79] = editorReady;
    $[80] = files;
    $[81] = handleFilesChange;
    $[82] = height;
    $[83] = markdownOnly;
    $[84] = previewReady;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] !== restoreState || $[87] !== t22 || $[88] !== t23 || $[89] !== unsavedChangesDialog) {
    t24 = <ClientThemeProvider><MarkdownEditorRestoreContext.Provider value={restoreState}>{t22}{t23}{unsavedChangesDialog}</MarkdownEditorRestoreContext.Provider></ClientThemeProvider>;
    $[86] = restoreState;
    $[87] = t22;
    $[88] = t23;
    $[89] = unsavedChangesDialog;
    $[90] = t24;
  } else {
    t24 = $[90];
  }
  return t24;
};
function _temp(file) {
  return file !== undefined;
}
function _temp2(file_0) {
  return {
    name: file_0.name,
    url: file_0.ufsUrl
  };
}
function _temp3(file_1) {
  return {
    name: file_1.name,
    url: file_1.ufsUrl
  };
}